(ns hai.tasks.card
  "Shared task row for the Tasks mini-app cards: the title and the assignee,
   over the deadline and the pill summing up what the task links to
   ([[hai.entity.related]]), on a flat card whose muted edge darkens on hover
   and which, pressed, opens the task over the whole card
   ([[hai.tasks.mini-app/editing-view]]), where its stamp is — for a viewer
   too, who gets it read-only. The assignee circle
   ([[hai.tasks.assignee-bubble]]) opens the picker, where an owner or admin
   reassigns the task in place. Required by the desktop and mobile Tasks
   cards and the extension sidebar so the row is one implementation, and its
   pieces by the tasks page's board, which adds the stampbox and its drag
   handle to them.

   Surface differences are passed in: `:change-fx` rides the reassignment's
   success; `:render-extra-actions`, if supplied, is called
   with the task and rendered left of the related pill; `:on-open-entity`
   goes to the related pill."
  (:require
   [garden.stylesheet]
   [hai.entity.related :as related]
   [hai.tasks.assignee-bubble :as bubble]
   [hai.tasks.data :as data]
   [hai.tasks.view :as view]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(o/defstyled task-row :div
  {:padding "13px 14px 12px"
   :display "flex" :flex-direction "column" :gap "5px"
   :position "relative"
   ;; The focus ring follows the card's corners.
   :border-radius "inherit"
   :cursor "pointer"}
  [:.task-row-main
   {:display "flex" :flex-direction "row"
    :gap "14px" :align-items "center"}]
  [:.task-title-block
   {:flex "1" :min-width "0"
    :display "flex" :flex-direction "column" :gap "2px"}]
  [:.task-title
   {:font-size "15px" :font-weight "700" :line-height "1.35"
    :color t/--card-forest
    :word-break "break-word"
    :overflow-wrap "anywhere"}]
  [:.task-title.done {:opacity "0.5" :text-decoration "line-through"}]
  [:.actions
   {:display "flex" :align-items "center" :gap "2px"}]
  ;; Under the title: the deadline at the left, the related pill in the corner,
  ;; and a host's extra actions just before it.
  [:.task-row-foot
   {:display "flex" :align-items "center" :justify-content "flex-end"
    :gap "10px"}
   [view/deadline-chip {:margin-right "auto"}]])

;; A task's description, flush beneath the row inside the pressed card, rising in
;; as one continuous sheet. Its left inset lines the text up under the title.
(o/defstyled task-detail :div
  {:padding "8px 18px 16px 48px"
   :border-top "1px solid" :border-top-color t/--card-forest-10
   :animation "task-detail-in 0.22s ease"}
  [:.task-desc
   {:font-size "13px" :line-height "1.55"
    :color t/--card-forest
    :white-space "pre-wrap"
    :opacity "0.8"}]
  [:&.done [:.task-desc {:opacity "0.55"}]])

;; The surface a task sits on. A task holding something back rests as a raised,
;; pressable card and presses down when opened; one with nothing to reveal stays
;; flat. Elevation — not a badge or chevron — is the signal that a task expands.
(o/defstyled task-shell :div
  {:border-radius "12px"
   :transition "transform 0.2s ease, box-shadow 0.2s ease, background 0.16s ease"}
  [:&.flat
   {:background t/--task-card-surface-flat
    :border-radius "13px"
    :transition "box-shadow 160ms ease"}]
  ;; The mini-app rows: a warmer surface, always edged — muted at rest — over a
  ;; soft shadow.
  [:&.edged
   {:background t/--task-card-surface-edged
    :border-radius "13px"
    :border-width "1px" :border-style "solid" :border-color t/--card-forest-10
    :box-shadow t/--task-card-shadow-edged
    :transition "border-color 160ms ease"}]
  [:&.raised
   {:background t/--task-card-surface
    :border-radius "16px"
    :box-shadow t/--task-card-shadow
    :cursor "pointer"}]
  ;; Pressed down: shallow shadow, and the detail extrudes inside the rounded box.
  [:&.raised.open
   {:box-shadow t/--task-card-shadow-pressed
    :overflow "hidden"}]
  ;; Hover cues only where hover exists — a tap's sticky :hover would leave the
  ;; card floating until the next tap elsewhere.
  [:at-media {:hover "hover"}
   ;; Only a collapsed card lifts; the lift is the "press me" cue.
   ["&.raised:not(.open):hover"
    {:transform "translateY(-7px)"
     :box-shadow t/--task-card-shadow-hover}]
   ;; A flat card only draws its edge.
   [:&.flat:hover
    {:box-shadow t/--task-card-ring-flat-hover}]
   ;; An edged card darkens its edge.
   [:&.edged:hover
    {:border-color t/--note-sage-35}]]
  ;; A just-added task holds a sage wash then fades — long enough to catch the
  ;; eye. A relative-positioned overlay keeps it off the card's own background
  ;; and drop shadow, so it works the same on flat rows and raised cards.
  [:&.just-added
   {:position "relative"}
   [:&::after
    {:content "''"
     :position "absolute" :inset "0"
     :border-radius "inherit"
     :pointer-events "none"
     :animation "task-just-added 2s ease-out forwards"}]])

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules just-added-keyframes
  (garden.stylesheet/at-keyframes
   :task-just-added
   ["0%"   {:background "rgba(45,62,42,0.16)"}]
   ["55%"  {:background "rgba(45,62,42,0.16)"}]
   ["100%" {:background "rgba(45,62,42,0)"}]))

(o/defstyled task-group :div
  {:transition "background 0.15s ease"}
  [:&.confirming
   {:background "rgba(177,58,48,0.05)"}])

(o/defstyled delete-confirm :div
  {:display "flex" :align-items "center" :justify-content "flex-end"
   :gap "8px"
   :padding "10px 22px 12px"
   :font-size "13px" :color t/--card-forest
   :border-bottom-width "1px" :border-bottom-style "solid" :border-bottom-color t/--property-3}
  [:.prompt {:margin-right "auto" :color t/--link-muted}]
  [:.delete-yes
   {:padding "6px 14px"
    :border "none" :border-radius "8px"
    :background t/--property-13 :color "white"
    :font-family "inherit"
    :font-size "13px" :font-weight "600"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--property-14}]]
  [:.delete-no
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :font-family "inherit"
    :font-size "13px" :color t/--link-muted
    :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]])

;;
;; The row's pieces, inside [[task-row]] on a [[task-shell]]. The mini-app's
;; row puts them together below; a host with more to a row — the tasks page's
;; board, with its stampbox and drag handle — puts them together itself.
;;

(defn pressable
  "The attributes that make a row press as a whole, to `on-press`: a click, or
   Enter or Space on the row itself — on the assignee or an action they are
   theirs."
  [on-press]
  {:role "button"
   :tab-index 0
   :on-click on-press
   :on-key-down (fn [e]
                  (when (and (= (.-target e) (.-currentTarget e))
                             (contains? #{"Enter" " "} (.-key e)))
                    (.preventDefault e)
                    (on-press)))})

(defn title-block
  "The task's title, struck through once it is done."
  [task]
  [:div.task-title-block
   [:div.task-title
    {:class (when (:task/completed-at task) "done")}
    (:task/title task)]])

(defn row-foot
  "Under the title: the deadline at the left while the task is open, a host's
   `extra-actions` (hiccup) and, in the corner, the pill summing up what the
   task links to, whose presses go to `on-open-entity`. Nothing when there is
   none of them."
  [{:keys [task extra-actions on-open-entity]}]
  (let [done? (some? (:task/completed-at task))
        deadline? (and (not done?) (some? (:task/deadline-at task)))
        related {:link-urls (vec (keep :activity/url (:task/activities task)))
                 :tasks (:task/tasks task)
                 :notes (:task/notes task)
                 :contacts (:task/contacts task)
                 :tags (:task/tags task)
                 :spaces (:entity/spaces task)}
        related? (boolean (some seq (vals related)))]
    (when (or deadline? related? extra-actions)
      [:div.task-row-foot
       (when deadline?
         [view/task-deadline-chip {:task task}])
       (when extra-actions
         [:div.actions {:on-click #(.stopPropagation %)}
          extra-actions])
       [related/component {:related related
                           :on-open-entity on-open-entity}]])))

(defn task-row-component
  "One task row for the mini-app cards; the surface-difference opts are
   described in the namespace docstring."
  [task & [{:keys [change-fx render-extra-actions on-open-entity]}]]
  (let [task-uuid (:task/uuid task)
        highlighted? @(<s [::data/highlighted? task-uuid])]
    [task-shell
     {:class (cond-> ["edged"]
               highlighted? (conj "just-added"))}
     [task-row
      (assoc (pressable #(>e [::data/open-editor task-uuid]))
             :class (when (:task/completed-at task) "done"))
      [:div.task-row-main
       [title-block task]
       [bubble/component {:task task :change-fx change-fx}]]
      [row-foot {:task task
                 :extra-actions (when render-extra-actions
                                  (render-extra-actions task))
                 :on-open-entity on-open-entity}]]]))
